/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from '../../utils/language.utils';
import { useTranslation } from '../../hooks/useTranslation';
import {
    useEntityCustomFieldDefinitions,
    type EntityCustomFieldDefinitionLoadState,
} from '../../hooks/useEntityCustomFieldDefinitions';
import {
    type EntityCustomFieldDefinition,
} from '../../utils/entity-custom-field-client';
import { toEntityCustomFieldFormValue } from '../../utils/entity-custom-field-codec';
import { SearchableSelect } from '../SearchableSelect';
import { MultiSelectDropdown } from '../MultiSelectDropdown';
import { DateTimeLocalInput } from './DateTimeLocalInput';
import { Alert } from './Alert';
import { UserGroupSelect } from './UserGroupSelect';
import type {
    BaseFormFieldType,
    FormErrors,
    SelectOption as ContractSelectOption,
} from './form-contract';
import {
    guidedLearningTargetSelector,
} from './guided-learning.contract';
import { registerGuidedLearningTarget } from './guided-learning.registry';
import { FormFieldGrid, buildLayoutFields, getFieldLearningCapabilities, type FormField, type LayoutFormField, type FormControlContext } from './FormFieldGrid';
import './FormBuilder.css';
import './SettingsFields.css';
import { DocumentValidationContext } from './document-validation-context';

export type FieldType = BaseFormFieldType;
export type SelectOption = ContractSelectOption;
export type { FormField } from './FormFieldGrid';

export interface FormBuilderProps {
    /** Shared settings rows keep the existing controls, validation and field IDs. */
    layout?: 'grid' | 'settings';
    fields: FormField[];
    values: any;                // Formun o anki değerleri (state)
    onChange: (values: any) => void; // Değer değiştiğinde tetiklenir
    /** Aynı yüzeyde birden çok builder olduğunda DOM alan kimliklerini benzersiz tutar. */
    idPrefix?: string;
    language?: SupportedLanguage;
    errors?: FormErrors; // Validasyon hataları (opsiyonel)
    /** BPM vb.: hata state’i dışarıdan yönetmek için (isteğe bağlı). */
    setErrors?: Dispatch<SetStateAction<FormErrors>>;
    /** Core entity custom fields: verildiğinde aktif ek alanlar otomatik ayrı tabda render edilir. */
    customFieldEntityType?: string;
    /** Firma kapsamı; boşsa global + mevcut entityType tanımları gösterilir. */
    customFieldCompanyId?: string | number | null;
    /** API'den çekmek yerine dışarıdan tanım geçmek isteyen ürün formları için. */
    customFields?: EntityCustomFieldDefinition[];
    /**
     * Otomatik ek alan tanım yüklemesinin açık durum sözleşmesi.
     * Kart sahibi `ready === false` iken kaydetmeyi kapatmalıdır.
     */
    onCustomFieldsLoadStateChange?: (state: EntityCustomFieldDefinitionLoadState) => void;
    /** Ek alan değerlerinin form state'indeki anahtarı. Varsayılan: customFields. */
    customFieldValueKey?: string;
    mainTabLabel?: string;
    customFieldsTabLabel?: string;
    customFieldsRenderMode?: 'tab' | 'inline';
}

type CustomFieldTabGroup = {
    key: string;
    label: string;
    fields: FormField[];
    layoutFields: LayoutFormField[];
};

export type { EntityCustomFieldDefinition } from '../../utils/entity-custom-field-client';
export type { EntityCustomFieldDefinitionLoadState } from '../../hooks/useEntityCustomFieldDefinitions';

const MAIN_FORM_TAB_KEY = '__main';
const DEFAULT_CUSTOM_FIELD_TAB_KEY = '__custom_fields';

function getTabLabel(language: SupportedLanguage, kind: 'main' | 'custom'): string {
    if (kind === 'main') return language === 'tr' ? 'Genel Bilgiler' : 'General';
    return language === 'tr' ? 'Ek Alanlar' : 'Custom Fields';
}

function cleanOptionalString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function getCustomFieldTabKey(field: EntityCustomFieldDefinition): string {
    const raw = cleanOptionalString(field.tabKey) || cleanOptionalString(field.tabLabel);
    return raw ? `custom:${raw}` : DEFAULT_CUSTOM_FIELD_TAB_KEY;
}

function getCustomFieldTabLabel(field: EntityCustomFieldDefinition, fallback: string): string {
    return cleanOptionalString(field.tabLabel) || fallback;
}

function getCustomFieldDataType(field: EntityCustomFieldDefinition): string {
    return String(field.dataType || 'text').trim().toLowerCase();
}

function normalizeCustomFieldOptions(optionsJson: unknown): SelectOption[] {
    if (optionsJson === null || optionsJson === undefined) return [];
    if (Array.isArray(optionsJson)) {
        return optionsJson
            .map<SelectOption | null>((item) => {
                if (item && typeof item === 'object') {
                    const raw = item as Record<string, unknown>;
                    const value = raw.value ?? raw.code ?? raw.id ?? raw.label ?? raw.name;
                    const label = raw.label ?? raw.name ?? raw.title ?? value;
                    if (value === undefined || value === null) return null;
                    return { value: String(value), label: String(label ?? value) };
                }
                if (item === undefined || item === null) return null;
                return { value: String(item), label: String(item) };
            })
            .filter((item): item is SelectOption => Boolean(item));
    }
    if (typeof optionsJson === 'object') {
        return Object.entries(optionsJson as Record<string, unknown>).map(([value, label]) => ({
            value,
            label: String(label ?? value),
        }));
    }
    if (typeof optionsJson === 'string') {
        return optionsJson
            .split(/\r?\n|,/)
            .map((item) => item.trim())
            .filter(Boolean)
            .map((item) => ({ value: item, label: item }));
    }
    return [];
}

function mapCustomFieldToFormField(field: EntityCustomFieldDefinition): FormField {
    const dataType = getCustomFieldDataType(field);
    const requestedColSpan = Number(field.colSpan);
    const colSpan = Number.isInteger(requestedColSpan) && requestedColSpan >= 1 && requestedColSpan <= 12
        ? requestedColSpan as FormField['colSpan']
        : undefined;
    const rows = Number.isInteger(Number(field.rows)) && Number(field.rows) > 0
        ? Number(field.rows)
        : undefined;
    const presentationMode = String(field.presentationMode || '').trim().toLowerCase();
    const common = {
        name: field.code,
        label: field.label,
        required: field.isRequired,
        description: field.description ?? undefined,
        placeholder: field.placeholder ?? undefined,
        disabled: field.disabled,
        colSpan: colSpan ?? 6 as const,
    };

    if (dataType === 'number') return { ...common, type: 'number', step: '0.000001' };
    if (dataType === 'date') return { ...common, type: 'date' };
    if (dataType === 'datetime') return { ...common, type: 'datetime' };
    if (dataType === 'boolean') return { ...common, type: 'checkbox', colSpan: colSpan ?? 4 };
    if (dataType === 'select') return { ...common, type: 'select', options: normalizeCustomFieldOptions(field.optionsJson) };
    if (dataType === 'json') return { ...common, type: 'textarea', rows: rows ?? 4, colSpan: colSpan ?? 12 };
    if (presentationMode === 'textarea' || presentationMode === 'multiline') {
        return { ...common, type: 'textarea', rows: rows ?? 4, colSpan: colSpan ?? 12 };
    }
    return { ...common, type: 'text' };
}

function getScopedCustomFields(
    definitions: EntityCustomFieldDefinition[],
    companyId?: string | number | null,
): EntityCustomFieldDefinition[] {
    const normalizedCompanyId = companyId === undefined || companyId === null || companyId === ''
        ? ''
        : String(companyId);
    return definitions
        .filter((field) => field.isActive !== false)
        .filter((field) => field.showInForm !== false)
        .filter((field) => {
            if (!normalizedCompanyId) return true;
            return field.companyId === null || field.companyId === undefined || String(field.companyId) === normalizedCompanyId;
        })
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
}

export const FormBuilder: React.FC<FormBuilderProps> = ({
    layout = 'grid',
    fields,
    values,
    onChange,
    idPrefix = '',
    language = DEFAULT_LANGUAGE,
    errors = {},
    setErrors: _setErrors,
    customFieldEntityType,
    customFieldCompanyId,
    customFields,
    onCustomFieldsLoadStateChange,
    customFieldValueKey = 'customFields',
    mainTabLabel,
    customFieldsTabLabel,
    customFieldsRenderMode = 'tab',
}) => {
    const t = useTranslation(language);
    const [activeTab, setActiveTab] = useState<string>(MAIN_FORM_TAB_KEY);
    const documentValidation = React.useContext(DocumentValidationContext);
    const customFieldLoadState = useEntityCustomFieldDefinitions({
        entityType: customFieldEntityType,
        companyId: customFieldCompanyId,
        providedDefinitions: customFields,
    });
    const customFieldsLoadStateCallbackRef = useRef(onCustomFieldsLoadStateChange);
    customFieldsLoadStateCallbackRef.current = onCustomFieldsLoadStateChange;

    const handleChange = (name: string, value: any) => {
        onChange({
            ...values,
            [name]: value
        });
    };

    const patchValues = (patch: Record<string, unknown>) => {
        onChange({
            ...values,
            ...patch,
        });
    };

    const layoutFields = buildLayoutFields(fields);
    const scopedCustomFields = useMemo(
        () => getScopedCustomFields([...customFieldLoadState.definitions], customFieldCompanyId),
        [customFieldCompanyId, customFieldLoadState.definitions],
    );
    const mainTabText = mainTabLabel ?? getTabLabel(language, 'main');
    const customFieldsTabText = customFieldsTabLabel ?? getTabLabel(language, 'custom');
    const customFormFields = useMemo(
        () => scopedCustomFields.map(mapCustomFieldToFormField),
        [scopedCustomFields],
    );
    const customLayoutFields = useMemo(
        () => buildLayoutFields(customFormFields),
        [customFormFields],
    );
    const customFieldTabs = useMemo<CustomFieldTabGroup[]>(() => {
        const tabs = new Map<string, { key: string; label: string; fields: FormField[] }>();
        for (const definition of scopedCustomFields) {
            const key = getCustomFieldTabKey(definition);
            const existing = tabs.get(key);
            const tab = existing ?? {
                key,
                label: getCustomFieldTabLabel(definition, customFieldsTabText),
                fields: [],
            };
            tab.fields.push(mapCustomFieldToFormField(definition));
            tabs.set(key, tab);
        }

        return Array.from(tabs.values()).map((tab) => ({
            ...tab,
            layoutFields: buildLayoutFields(tab.fields),
        }));
    }, [customFieldsTabText, scopedCustomFields]);
    const hasCustomFields = customFormFields.length > 0;
    const renderCustomFieldsInline = customFieldsRenderMode === 'inline';
    const hasTabbedCustomFields = hasCustomFields && !renderCustomFieldsInline;
    const customFieldValues = values?.[customFieldValueKey] && typeof values[customFieldValueKey] === 'object'
        ? values[customFieldValueKey]
        : {};

    useEffect(() => {
        customFieldsLoadStateCallbackRef.current?.(customFieldLoadState);
    }, [customFieldLoadState]);

    useEffect(() => {
        if (!hasCustomFields && activeTab !== MAIN_FORM_TAB_KEY) {
            setActiveTab(MAIN_FORM_TAB_KEY);
        }
    }, [activeTab, hasCustomFields]);

    useEffect(() => {
        if (!hasTabbedCustomFields) return;
        const tabExists = activeTab === MAIN_FORM_TAB_KEY || customFieldTabs.some((tab) => tab.key === activeTab);
        if (!tabExists) setActiveTab(MAIN_FORM_TAB_KEY);
    }, [activeTab, customFieldTabs, hasTabbedCustomFields]);

    useEffect(() => {
        if (!hasCustomFields) return;
        const patch: Record<string, unknown> = {};
        for (const field of scopedCustomFields) {
            if (field.defaultValue === undefined || field.defaultValue === null) continue;
            if (customFieldValues[field.code] !== undefined) continue;
            patch[field.code] = toEntityCustomFieldFormValue(field, field.defaultValue);
        }
        if (Object.keys(patch).length === 0) return;
        onChange({
            ...values,
            [customFieldValueKey]: {
                ...customFieldValues,
                ...patch,
            },
        });
    }, [customFieldValueKey, customFieldValues, hasCustomFields, onChange, scopedCustomFields, values]);

    useEffect(() => {
        const unregister = fields.flatMap((field) => {
            const targetKey = field.learningTargetId?.trim();
            const capabilities = getFieldLearningCapabilities(field);
            const selector = targetKey ? guidedLearningTargetSelector(targetKey) : '';
            if (!targetKey || !selector || capabilities.length === 0) return [];
            return [registerGuidedLearningTarget({
                key: targetKey,
                capabilities,
                resolveElement: () => document.querySelector<HTMLElement>(selector),
                execute: async (command) => {
                    const element = document.querySelector<HTMLElement>(selector);
                    if (!element) throw new Error('GUIDED_LEARNING_TARGET_UNAVAILABLE');
                    if (command.capability === 'FOCUS') {
                        const focusTarget = element.matches('input, textarea, button, [tabindex]')
                            ? element
                            : element.querySelector<HTMLElement>('input, textarea, button, [tabindex]');
                        focusTarget?.focus();
                        return;
                    }
                    if (command.capability === 'SET_VALUE') {
                        onChange({ ...values, [field.name]: command.value ?? '' });
                        return;
                    }
                    if (command.capability === 'ACTIVATE' && field.type === 'checkbox') {
                        onChange({ ...values, [field.name]: !Boolean(values[field.name]) });
                        return;
                    }
                    if (command.capability === 'SELECT') {
                        const option = field.options?.find((candidate) => candidate.learningOptionKey === command.optionKey);
                        if (!option || option.disabled) throw new Error('GUIDED_LEARNING_OPTION_NOT_FOUND');
                        if (field.type === 'multiselect') {
                            const current = Array.isArray(values[field.name]) ? values[field.name] : [];
                            const next = current.some((item: unknown) => String(item) === String(option.value))
                                ? current
                                : [...current, option.value];
                            onChange({ ...values, [field.name]: next });
                        } else {
                            onChange({ ...values, [field.name]: option.value });
                        }
                        return;
                    }
                    throw new Error('GUIDED_LEARNING_CAPABILITY_NOT_ALLOWED');
                },
            })];
        });
        return () => unregister.forEach((dispose) => dispose());
    }, [fields, onChange, values]);

    const containerClassName = `form-builder-container${hasTabbedCustomFields ? ' form-builder-container-tabbed' : ''}`;

    const renderControl = ({ field, fieldId, fieldValues, handleFieldChange, isError, fieldDescribedBy, learningTargetId }: FormControlContext) => (
        <>
                                {field.type === 'datetime' && (
                                    <DateTimeLocalInput
                                        id={fieldId}
                                        aria-label={field.label}
                                        value={fieldValues[field.name] === undefined ? '' : String(fieldValues[field.name] ?? '')}
                                        onChange={(v) => handleFieldChange(field.name, v)}
                                        disabled={field.disabled}
                                        required={field.required}
                                        className={isError ? 'input-error' : undefined}
                                    />
                                )}

                                {/* SELECT – SearchableSelect: açılan liste CSS ile çerçeveli (dark mod uyumlu) */}
                                {field.type === 'select' && field.presentationMode === 'user-group' && (
                                    <div className={isError ? 'input-error' : ''}>
                                        <UserGroupSelect
                                            id={fieldId}
                                            options={(field.options ?? [])
                                                .filter((option) => option.kind)
                                                .map((option) => ({
                                                    value: String(option.value),
                                                    label: option.label,
                                                    kind: option.kind!,
                                                    searchStr: option.searchStr,
                                                    disabled: option.disabled,
                                                }))}
                                            value={fieldValues[field.name] === undefined ? '' : String(fieldValues[field.name])}
                                            onChange={(valueStr) => {
                                                const option = field.options?.find((candidate) => String(candidate.value) === valueStr);
                                                handleFieldChange(field.name, option !== undefined ? option.value : valueStr);
                                            }}
                                            placeholder={field.placeholder || t.common.select}
                                            searchPlaceholder={language === 'tr' ? 'Kullanıcı veya grup ara...' : 'Search users or groups...'}
                                            noResultsText={language === 'tr' ? 'Kullanıcı veya grup bulunamadı.' : 'No users or groups found.'}
                                            disabled={field.disabled}
                                            ariaLabel={field.label}
                                        />
                                    </div>
                                )}

                                {field.type === 'select' && field.presentationMode !== 'user-group' && (
                                    <div className={isError ? 'input-error' : ''}>
                                        <SearchableSelect
                                            id={fieldId}
                                            options={[
                                                { value: '', label: t.common.select },
                                                ...(field.options ?? []).map((opt) => ({
                                                    value: String(opt.value),
                                                    label: opt.label,
                                                    icon: opt.icon,
                                                    description: opt.description,
                                                    iconSrc: opt.iconSrc,
                                                    iconDarkSrc: opt.iconDarkSrc,
                                                    searchStr: opt.searchStr,
                                                    disabled: opt.disabled,
                                                    learningOptionKey: opt.learningOptionKey,
                                                }))
                                            ]}
                                            value={fieldValues[field.name] === undefined ? '' : String(fieldValues[field.name])}
                                            onChange={(valueStr) => {
                                                const opt = field.options?.find((o) => String(o.value) === valueStr);
                                                handleFieldChange(field.name, opt !== undefined ? opt.value : valueStr);
                                            }}
                                            placeholder={t.common.select}
                                            disabled={field.disabled}
                                            searchable={true}
                                            onSearchChange={field.onSearchChange}
                                            required={field.required}
                                            ariaLabel={field.label}
                                            ariaDescribedBy={fieldDescribedBy}
                                            ariaInvalid={isError}
                                            learningTargetId={learningTargetId}
                                        />
                                    </div>
                                )}

                                {/* MULTI SELECT */}
                                {field.type === 'multiselect' && (
                                    <div className={isError ? 'input-error' : ''}>
                                        <MultiSelectDropdown
                                            id={fieldId}
                                            options={(field.options ?? []).map((opt) => ({
                                                value: String(opt.value),
                                                label: opt.label,
                                                searchStr: opt.searchStr,
                                                group: opt.group,
                                            }))}
                                            value={Array.isArray(fieldValues[field.name])
                                                ? fieldValues[field.name].map((item: unknown) => String(item))
                                                : []}
                                            onChange={(nextValue) => handleFieldChange(field.name, nextValue)}
                                            placeholder={field.placeholder || t.common.select}
                                            disabled={field.disabled}
                                            searchable={true}
                                            searchPlaceholder={field.searchPlaceholder}
                                            selectAllLabel={t.common.selectAll || t.common.select}
                                            selectedCountLabel={field.selectedCountLabel ?? ((count) => language === 'tr' ? `${count} seçili` : `${count} selected`)}
                                            showSelectAll={field.showSelectAll}
                                            noResultsText={field.noResultsText}
                                            onSearchChange={field.onSearchChange}
                                        />
                                    </div>
                                )}


        </>
    );

    const renderFieldGrid = (fields: LayoutFormField[], values: any, onChange: (name: string, value: any) => void) => (
        <FormFieldGrid layout={layout} fields={fields} values={values} onChange={onChange}
            onPatch={patchValues} idPrefix={idPrefix} errors={errors} renderControl={renderControl} />
    );

    const handleCustomFieldsChange = (name: string, value: any) => {
        onChange({
            ...values,
            [customFieldValueKey]: {
                ...customFieldValues,
                [name]: value,
            },
        });
    };

    return (
        <div
            className={containerClassName}
            onInvalidCapture={(event) => {
                if (!documentValidation) return;
                const panel = (event.target as HTMLElement).closest<HTMLElement>('[data-form-builder-tab]');
                if (panel?.parentElement === event.currentTarget) setActiveTab(panel.dataset.formBuilderTab!);
            }}
            aria-busy={customFieldLoadState.loading || undefined}
            data-custom-fields-status={customFieldLoadState.status}
        >
            {customFieldLoadState.source === 'remote' && customFieldLoadState.loading ? (
                <div className="form-field-description" role="status" aria-live="polite">
                    {t.common.formCustomFieldsLoading}
                </div>
            ) : null}
            {customFieldLoadState.source === 'remote' && customFieldLoadState.error ? (
                <Alert type="error" dismissible={false}>
                    <span>
                        {t.common.formCustomFieldsLoadError}
                    </span>{' '}
                    <button type="button" className="btn-secondary" onClick={customFieldLoadState.retry}>
                        {t.common.formCustomFieldsRetry}
                    </button>
                </Alert>
            ) : null}
            {hasTabbedCustomFields && (
                <div className="form-builder-tabs" role="tablist" aria-label={customFieldsTabText}>
                    <button
                        type="button"
                        className={`form-builder-tab ${activeTab === MAIN_FORM_TAB_KEY ? 'active' : ''}`}
                        role="tab"
                        aria-selected={activeTab === MAIN_FORM_TAB_KEY}
                        onClick={() => setActiveTab(MAIN_FORM_TAB_KEY)}
                    >
                        {mainTabText}
                    </button>
                    {customFieldTabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={`form-builder-tab ${activeTab === tab.key ? 'active' : ''}`}
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            {(!hasCustomFields || activeTab === MAIN_FORM_TAB_KEY || renderCustomFieldsInline || documentValidation) && layoutFields.length > 0 && (
                hasTabbedCustomFields && documentValidation ? (
                    <div data-form-builder-tab={MAIN_FORM_TAB_KEY} hidden={activeTab !== MAIN_FORM_TAB_KEY}>
                        {renderFieldGrid(layoutFields, values, handleChange)}
                    </div>
                ) : renderFieldGrid(layoutFields, values, handleChange)
            )}

            {hasCustomFields && renderCustomFieldsInline &&
                renderFieldGrid(customLayoutFields, customFieldValues, handleCustomFieldsChange)}

            {hasTabbedCustomFields && customFieldTabs.map((tab) => activeTab === tab.key || documentValidation ? (
                <div key={tab.key} className="form-builder-tab-panel" role="tabpanel"
                    data-form-builder-tab={tab.key} hidden={activeTab !== tab.key}>
                    {renderFieldGrid(tab.layoutFields, customFieldValues, handleCustomFieldsChange)}
                </div>
            ) : null)}
        </div>
    );
};
